/**
 * TB Mukt Bharat - Messages Page
 * Full-featured messaging with GovLayout
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  GovLayout,
  GovPageTitle,
  GovCard,
  GovButton,
  GovEmptyState
} from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import chatService from '@/services/chatService'
import { getApiErrorMessage } from '@/services/api'
import {
  MessageCircle, Send, Search, Users, UserPlus,
  Phone, Video, Check, CheckCheck, Clock,
  Smile, Image as ImageIcon, X, ArrowLeft, MoreHorizontal,
  Trash2, Copy, CornerUpLeft, Edit3, File
} from 'lucide-react'
import EmojiPicker from 'emoji-picker-react'

// ================================================================================
// TYPES
// ================================================================================
interface Conversation {
  roomId?: number
  id?: number
  otherUserId?: number
  groupId?: number
  type: 'DIRECT' | 'GROUP'
  displayName?: string
  name?: string
  otherUserName?: string
  avatar?: string
  otherUserAvatar?: string
  lastMessage?: string
  lastMessageTime?: string
  unreadCount?: number
  isOnline?: boolean
  memberCount?: number
  members?: any[]
}

interface Message {
  id: number | string
  content?: string
  senderId: number
  receiverId?: number
  groupId?: number
  timestamp?: string
  createdAt?: string
  status?: string
  messageType?: string
  mediaUrl?: string
  fileUrl?: string
  fileName?: string
  replyTo?: Message
  isDeleted?: boolean
  isEdited?: boolean
  sender?: { id: number; firstName: string; lastName: string; profileImage?: string }
}

// ================================================================================
// CONVERSATION LIST ITEM - Memoized
// ================================================================================
const ConversationItem = React.memo<{
  conversation: Conversation
  isActive: boolean
  currentUserId: number
  onClick: () => void
}>(({ conversation, isActive, currentUserId, onClick }) => {
  const displayName = conversation.displayName || conversation.otherUserName || conversation.name || 'Unknown'
  const avatar = conversation.avatar || conversation.otherUserAvatar
  const getMediaUrl = (url: string | undefined) => {
    if (!url) return ''
    if (url.startsWith('http')) return url
    if (url.startsWith('/uploads/')) return url
    if (url.startsWith('/api/')) return url
    return '/api/uploads/' + url.replace(/^\//, '')
  }

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 flex items-center gap-3 text-left transition-colors rounded-lg mb-1 ${
        isActive ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50'
      }`}
    >
      <div className="relative flex-shrink-0">
        {avatar ? (
          <img src={getMediaUrl(avatar)} alt={displayName} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
            {conversation.type === 'GROUP' ? <Users className="w-6 h-6" /> : displayName[0]}
          </div>
        )}
        {conversation.isOnline && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-gray-900 truncate">{displayName}</p>
          {conversation.lastMessageTime && (
            <span className="text-xs text-gray-400">{formatTime(conversation.lastMessageTime)}</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-sm text-gray-500 truncate">{conversation.lastMessage || 'No messages'}</p>
          {conversation.unreadCount && conversation.unreadCount > 0 && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  )
})

function formatTime(date: string | undefined) {
  if (!date) return ''
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// ================================================================================
// MESSAGE BUBBLE - Memoized
// ================================================================================
interface MessageBubbleProps {
  message: Message
  isOwn: boolean
  currentUserId: number
  onReply: () => void
  onEdit: () => void
  onDelete: (forEveryone: boolean) => void
  showOptions: number | null
  setShowOptions: (id: number | null) => void
}

const MessageBubble = React.memo<MessageBubbleProps>(({
  message,
  isOwn,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  showOptions,
  setShowOptions
}) => {
  const getMediaUrl = (url: string | undefined) => {
    if (!url) return ''
    if (url.startsWith('http')) return url
    if (url.startsWith('/uploads/')) return url
    if (url.startsWith('/api/')) return url
    return '/api/uploads/' + url.replace(/^\//, '')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SENDING': return <Clock className="w-3.5 h-3.5 text-gray-400" />
      case 'SENT': return <Check className="w-3.5 h-3.5 text-gray-400" />
      case 'DELIVERED': return <CheckCheck className="w-3.5 h-3.5 text-gray-400" />
      case 'READ': return <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />
      default: return <Clock className="w-3.5 h-3.5 text-gray-400" />
    }
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className={`relative group max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-blue-600 text-white rounded-br-md'
              : 'bg-white text-gray-800 shadow-sm rounded-bl-md'
          } ${message.isDeleted ? 'italic opacity-70' : ''}`}
        >
          {message.isDeleted ? (
            <p className="text-sm">This message was deleted</p>
          ) : (
            <>
              {message.replyTo && (
                <div className={`text-xs mb-1 p-2 rounded ${isOwn ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <p className="font-medium truncate">{message.replyTo.content?.slice(0, 50)}...</p>
                </div>
              )}
              {message.messageType === 'IMAGE' && message.mediaUrl && (
                <img src={getMediaUrl(message.mediaUrl)} alt="" className="max-w-[200px] rounded-lg mb-1" loading="lazy" />
              )}
              {message.messageType === 'VIDEO' && message.mediaUrl && (
                <video src={getMediaUrl(message.mediaUrl)} controls className="max-w-[200px] rounded-lg mb-1" />
              )}
              {message.messageType === 'FILE' && message.fileName && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isOwn ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <File className="w-5 h-5" />
                  <a href={getMediaUrl(message.fileUrl || message.mediaUrl)} target="_blank" rel="noopener noreferrer" className="text-sm underline">
                    {message.fileName}
                  </a>
                </div>
              )}
              {message.content && <p className="text-sm whitespace-pre-wrap">{message.content}</p>}
            </>
          )}
          <div className={`flex items-center gap-1 mt-1 text-xs ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
            <span>{formatTime(message.createdAt || message.timestamp)}</span>
            {isOwn && getStatusIcon(message.status || 'SENT')}
            {message.isEdited && <span>(edited)</span>}
          </div>
        </div>

        {/* Message Options */}
        {!message.isDeleted && (
          <button
            onClick={() => setShowOptions(showOptions === message.id ? null : message.id as number)}
            className={`absolute -top-2 ${isOwn ? '-left-8' : '-right-8'} p-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow`}
          >
            <MoreHorizontal className="w-4 h-4 text-gray-600" />
          </button>
        )}

        {showOptions === message.id && (
          <div className={`absolute top-6 ${isOwn ? 'left-0' : 'right-0'} bg-white rounded-lg shadow-lg border py-1 z-10 min-w-[140px]`}>
            <button onClick={() => { onReply(); setShowOptions(null) }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
              <CornerUpLeft className="w-4 h-4" /> Reply
            </button>
            <button onClick={() => { navigator.clipboard.writeText(message.content || ''); setShowOptions(null) }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
              <Copy className="w-4 h-4" /> Copy
            </button>
            {isOwn && !message.isDeleted && (
              <>
                <button onClick={() => { onEdit(); setShowOptions(null) }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
                  <Edit3 className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => { onDelete(true); setShowOptions(null) }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> Unsend
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

// ================================================================================
// NEW CHAT MODAL
// ================================================================================
interface NewChatModalProps {
  isOpen: boolean
  onClose: () => void
  onStartChat: (user: any) => void
  currentUserId: number
}

const NewChatModal: React.FC<NewChatModalProps> = ({ isOpen, onClose, onStartChat, currentUserId }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await chatService.searchUsers(searchQuery)
        if (response.success) {
          const users = Array.isArray(response.data?.content) ? response.data.content :
                       Array.isArray(response.data) ? response.data : []
          setSearchResults(users.filter((u: any) => u.id !== currentUserId))
        }
      } catch (error) {
        console.error('Error searching users:', error)
      } finally {
        setLoading(false)
      }
    }, 300)
  }, [searchQuery, currentUserId])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">New Chat</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4 text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchQuery ? 'No users found' : 'Search for users to start a chat'}
            </div>
          ) : (
            searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => { onStartChat(user); onClose() }}
                className="w-full p-3 flex items-center gap-3 hover:bg-gray-50"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </div>
                <div className="text-left">
                  <p className="font-medium">{user.firstName} {user.lastName}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ================================================================================
// NEW GROUP MODAL
// ================================================================================
interface NewGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateGroup: (name: string, members: number[]) => void
  currentUserId: number
}

const NewGroupModal: React.FC<NewGroupModalProps> = ({ isOpen, onClose, onCreateGroup, currentUserId }) => {
  const [groupName, setGroupName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedMembers, setSelectedMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await chatService.searchUsers(searchQuery)
        if (response.success) {
          const users = Array.isArray(response.data?.content) ? response.data.content :
                       Array.isArray(response.data) ? response.data : []
          setSearchResults(users.filter((u: any) => u.id !== currentUserId))
        }
      } catch (error) {
        console.error('Error searching users:', error)
      } finally {
        setLoading(false)
      }
    }, 300)
  }, [searchQuery, currentUserId])

  const toggleMember = (user: any) => {
    setSelectedMembers(prev => {
      const exists = prev.find(m => m.id === user.id)
      if (exists) return prev.filter(m => m.id !== user.id)
      return [...prev, user]
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Create New Group</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <input
            type="text"
            placeholder="Group name..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="w-full px-4 py-2 bg-gray-100 rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Add members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {selectedMembers.length > 0 && (
          <div className="px-4 py-2 border-t bg-gray-50">
            <p className="text-xs text-gray-500 mb-2">Selected ({selectedMembers.length})</p>
            <div className="flex flex-wrap gap-2">
              {selectedMembers.map((member) => (
                <span key={member.id} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                  {member.firstName} {member.lastName}
                  <button onClick={() => toggleMember(member)} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-48 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4 text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              Search for users to add to the group
            </div>
          ) : (
            searchResults.map((user) => {
              const isSelected = selectedMembers.find(m => m.id === user.id)
              return (
                <button
                  key={user.id}
                  onClick={() => toggleMember(user)}
                  className={`w-full p-3 flex items-center gap-3 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                </button>
              )
            })
          )}
        </div>

        <div className="p-4 border-t">
          <button
            onClick={() => { onCreateGroup(groupName, selectedMembers.map(m => m.id)); onClose() }}
            disabled={!groupName.trim() || selectedMembers.length === 0}
            className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Group
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================================
// MAIN MESSAGES PAGE
// ================================================================================
const MessagesPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const currentUserId = user?.id

  // When the user clicks "Chat" on a joined community group, GroupsPage navigates here
  // with { state: { openConversationId, type } }. We open that conversation once the
  // list has loaded (the chat may be empty — that's expected).
  const openConversationId = (location.state as any)?.openConversationId as number | undefined
  const openConversationType = (location.state as any)?.type as string | undefined
  const openedFromGroupsRef = useRef(false)

  // State
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [activeTab, setActiveTab] = useState<'chats' | 'groups'>('chats')

  // New features state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showNewChatModal, setShowNewChatModal] = useState(false)
  const [showNewGroupModal, setShowNewGroupModal] = useState(false)
  const [typingUsers, setTypingUsers] = useState<Record<number, { isTyping: boolean; userName: string }>>({})
  const [showMessageOptions, setShowMessageOptions] = useState<number | null>(null)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [editingMessage, setEditingMessage] = useState<Message | null>(null)
  const [editText, setEditText] = useState('')
  const [uploadingFile, setUploadingFile] = useState(false)
  const [onlineUsers, setOnlineUsers] = useState<Record<number, boolean>>({})

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)
  const activeRoomRef = useRef<number | null>(null)
  const typingTimeoutRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await chatService.getConversations()
      if (response.success) {
        setConversations(response.data?.content || response.data || [])
      }
    } catch (error) {
      console.error('Error fetching conversations:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch messages
  const fetchMessages = useCallback(async (conversationId: number) => {
    if (!conversationId) return
    setLoadingMessages(true)
    activeRoomRef.current = conversationId
    try {
      const response = await chatService.getMessages(conversationId)
      if (response.success) {
        const msgs = response.data?.content || response.data?.messages || response.data || []
        const sortedMsgs = [...msgs].sort((a, b) => {
          const timeA = new Date(a.timestamp || a.createdAt).getTime()
          const timeB = new Date(b.timestamp || b.createdAt).getTime()
          return timeA - timeB
        })
        setMessages(sortedMsgs)
        chatService.joinRoom(conversationId)
        chatService.markAsRead(conversationId).catch(() => {})
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
    } finally {
      setLoadingMessages(false)
    }
  }, [])

  // Fetch online status
  useEffect(() => {
    const fetchOnlineStatus = async () => {
      if (activeConversation?.otherUserId) {
        try {
          const response = await chatService.getOnlineStatus(activeConversation.otherUserId)
          if (response.success) {
            setOnlineUsers(prev => ({
              ...prev,
              [activeConversation.otherUserId!]: response.data?.isOnline || false
            }))
          }
        } catch (error) {
          console.error('Error fetching online status:', error)
        }
      }
    }
    fetchOnlineStatus()
    const interval = setInterval(fetchOnlineStatus, 30000)
    return () => clearInterval(interval)
  }, [activeConversation?.otherUserId])

  // Connect WebSocket
  useEffect(() => {
    if (currentUserId) {
      const token = localStorage.getItem('token')
      chatService.connectWebSocket(currentUserId, token)
        .then(() => setIsConnected(true))
        .catch(err => console.error('WebSocket connection error:', err))

      const unsubMessage = chatService.onMessage((data) => {
        const currentRoomId = activeRoomRef.current
        if (data.id && !data.isEdit && !data.isDelete) {
          if (currentRoomId && data.conversationId === currentRoomId) {
            setMessages(prev => {
              const exists = prev.some(m => m.id === data.id)
              if (exists) return prev
              return [...prev, data]
            })
          }
          fetchConversations()
        }
        if (data.isEdit) {
          setMessages(prev => prev.map(m => m.id === data.id ? { ...m, content: data.content, isEdited: true } : m))
        }
        if (data.isDelete) {
          setMessages(prev => prev.map(m => m.id === data.id ? { ...m, isDeleted: true, content: 'This message was deleted' } : m))
        }
      })

      const unsubTyping = chatService.onTyping((data) => {
        if (activeRoomRef.current && data.roomId === activeRoomRef.current) {
          setTypingUsers(prev => ({
            ...prev,
            [data.userId]: { isTyping: data.isTyping, userName: data.userName }
          }))
        }
      })

      const unsubConn = chatService.onConnectionChange((connected) => {
        setIsConnected(connected)
      })

      return () => {
        unsubMessage()
        unsubTyping()
        unsubConn()
      }
    }
  }, [currentUserId, fetchConversations])

  // Initial fetch
  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Handle typing
  const handleTyping = useCallback(() => {
    if (!activeConversation?.roomId) return
    chatService.setTypingStatus(activeConversation.roomId, true)
    if (typingTimeoutRef.current[activeConversation.roomId]) {
      clearTimeout(typingTimeoutRef.current[activeConversation.roomId])
    }
    typingTimeoutRef.current[activeConversation.roomId] = setTimeout(() => {
      chatService.setTypingStatus(activeConversation.roomId!, false)
    }, 3000)
  }, [activeConversation])

  // Select conversation
  const selectConversation = useCallback((conv: Conversation) => {
    const normalizedConv: Conversation = {
      ...conv,
      displayName: conv.displayName || conv.otherUserName || conv.name || 'Unknown',
      avatar: conv.avatar || conv.otherUserAvatar,
      otherUserId: conv.otherUserId,
      type: conv.type || 'DIRECT',
      roomId: conv.roomId || conv.groupId || conv.id
    }
    setActiveConversation(normalizedConv)
    activeRoomRef.current = normalizedConv.roomId || null
    fetchMessages(normalizedConv.roomId!)
    setTypingUsers({})
    setReplyTo(null)
    setEditingMessage(null)
    inputRef.current?.focus()
  }, [fetchMessages])

  // Auto-open a conversation we were asked to open via router state (from Groups "Chat").
  // Placed after selectConversation so the reference is already declared.
  useEffect(() => {
    if (!openConversationId || openedFromGroupsRef.current) return
    if (loading || conversations.length === 0) return
    const conv = conversations.find(c => (c.roomId || c.id) === openConversationId)
    if (conv) {
      openedFromGroupsRef.current = true
      // Show it under the right tab so it's also visible in the sidebar list
      if (conv.type === 'GROUP' || openConversationType === 'GROUP') {
        setActiveTab('groups')
      }
      selectConversation(conv)
      // Clear the nav state so a refresh / Back button doesn't reopen it
      navigate('/messages', { replace: true, state: null })
    }
  }, [openConversationId, openConversationType, loading, conversations, selectConversation, navigate])

  // Send message
  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !activeConversation || !currentUserId) return

    const content = newMessage.trim()
    setNewMessage('')
    setReplyTo(null)

    const tempId = `temp-${Date.now()}`
    const optimisticMessage: Message = {
      id: tempId,
      content,
      senderId: currentUserId,
      receiverId: activeConversation.otherUserId,
      groupId: activeConversation.groupId,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      status: 'SENDING',
      messageType: 'TEXT',
      replyTo: replyTo || undefined
    }
    setMessages(prev => [...prev, optimisticMessage])

    try {
      const response = await chatService.sendMessage({
        content,
        messageType: 'TEXT',
        receiverId: activeConversation.otherUserId,
        groupId: activeConversation.groupId,
        roomId: activeConversation.roomId,
        replyToId: replyTo?.id ? Number(replyTo.id) : null
      })

      if (response.success) {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...response.data, status: 'DELIVERED' } : m
        ))
        fetchConversations()
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setMessages(prev => prev.filter(m => m.id !== tempId))
      setNewMessage(content)
    }
  }, [newMessage, activeConversation, currentUserId, fetchConversations, replyTo])

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'IMAGE' | 'VIDEO' | 'FILE') => {
    const file = e.target.files?.[0]
    if (!file || !activeConversation || !currentUserId) return

    if (file.size > 20 * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(1)
      alert(`This file is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller file.`)
      e.target.value = ''
      return
    }

    setUploadingFile(true)
    const tempId = `temp-${Date.now()}`

    const optimisticMessage: Message = {
      id: tempId,
      content: `Uploading ${file.name}...`,
      senderId: currentUserId,
      receiverId: activeConversation.otherUserId,
      groupId: activeConversation.groupId,
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      status: 'SENDING',
      messageType: type,
      fileName: file.name
    }
    setMessages(prev => [...prev, optimisticMessage])

    try {
      const formData = new FormData()
      formData.append('file', file)
      if (activeConversation.roomId) {
        formData.append('conversationId', activeConversation.roomId.toString())
      } else if (activeConversation.otherUserId) {
        formData.append('receiverId', activeConversation.otherUserId.toString())
      }
      formData.append('messageType', type)

      const response = await chatService.sendMediaMessage(formData)
      if (response.success) {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...response.data, status: 'DELIVERED' } : m
        ))
        fetchConversations()
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      alert(getApiErrorMessage(error, 'Failed to upload file. Please try again.'))
      setMessages(prev => prev.filter(m => m.id !== tempId))
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (videoInputRef.current) videoInputRef.current.value = ''
      if (docInputRef.current) docInputRef.current.value = ''
    }
  }

  // Delete message
  const deleteMessage = async (messageId: number, forEveryone: boolean) => {
    try {
      await chatService.deleteMessage(messageId, forEveryone ? 'everyone' : 'me')
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, isDeleted: true, content: 'This message was deleted' } : m
      ))
    } catch (error) {
      console.error('Error deleting message:', error)
    }
  }

  // Edit message
  const saveEditMessage = async () => {
    if (!editingMessage || !editText.trim()) return
    try {
      const response = await chatService.editMessage(editingMessage.id as number, editText)
      if (response.success) {
        setMessages(prev => prev.map(m =>
          m.id === editingMessage.id ? { ...m, content: editText, isEdited: true } : m
        ))
        setEditingMessage(null)
        setEditText('')
      }
    } catch (error) {
      console.error('Error editing message:', error)
    }
  }

  // Start conversation
  const startConversation = async (targetUser: any) => {
    try {
      const response = await chatService.getOrCreateDirectConversation(targetUser.id)
      if (response.success) {
        fetchConversations()
        selectConversation({
          ...response.data,
          otherUserId: targetUser.id,
          otherUserName: `${targetUser.firstName} ${targetUser.lastName}`,
          type: 'DIRECT'
        })
      }
    } catch (error) {
      console.error('Error starting conversation:', error)
    }
  }

  // Create group
  const createGroup = async (name: string, members: number[]) => {
    try {
      const response = await chatService.createGroupConversation(name, members)
      if (response.success || response.data) {
        fetchConversations()
        selectConversation({
          ...response.data,
          type: 'GROUP'
        })
      }
    } catch (error) {
      console.error('Error creating group:', error)
    }
  }

  // Typing indicator text
  const getTypingText = () => {
    const typing = Object.values(typingUsers).filter(t => t.isTyping)
    if (typing.length === 0) return null
    if (typing.length === 1) return `${typing[0].userName} is typing...`
    return 'Several people are typing...'
  }

  // Filtered conversations - memoized
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations
    return conversations.filter(conv => {
      const name = conv.otherUserName || conv.name || ''
      return name.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [conversations, searchQuery])

  // Conversations by tab - memoized
  const conversationsByTab = useMemo(() => {
    return filteredConversations.filter(conv =>
      activeTab === 'groups' ? conv.type === 'GROUP' : conv.type !== 'GROUP'
    )
  }, [filteredConversations, activeTab])

  return (
    <GovLayout>
      <GovPageTitle
        title="Messages"
        subtitle="Connect with healthcare workers and patients"
        icon={MessageCircle}
      />

      <GovCard className="p-0 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 h-[calc(100vh-180px)] min-h-[400px]">
          {/* Conversations Sidebar */}
          <div className={`${activeConversation ? 'hidden lg:flex' : 'flex'} col-span-12 lg:col-span-4 border-r flex-col bg-gray-50 h-full`}>
            {/* Header with New Chat/Group buttons */}
            <div className="p-3 border-b bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">Messages</h2>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setShowNewChatModal(true)} className="p-2 hover:bg-gray-100 rounded-full" title="New Chat">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                  </button>
                  <button onClick={() => setShowNewGroupModal(true)} className="p-2 hover:bg-gray-100 rounded-full" title="New Group">
                    <UserPlus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            {/* Connection Status */}
            {!isConnected && (
              <div className="px-4 py-2 bg-yellow-50 text-yellow-700 text-sm text-center">
                Reconnecting...
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b bg-white sticky top-0 z-10">
              <button
                onClick={() => setActiveTab('chats')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'chats'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MessageCircle className="w-4 h-4 inline mr-1" /> Chats
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'groups'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" /> Groups
              </button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-sm">Loading conversations...</p>
                </div>
              ) : conversationsByTab.length === 0 ? (
                <GovEmptyState
                  icon={activeTab === 'groups' ? '👥' : '💬'}
                  title={searchQuery ? 'No results' : activeTab === 'groups' ? 'No groups yet' : 'No chats yet'}
                  description={searchQuery ? 'Try a different search' : 'Start a new conversation'}
                />
              ) : (
                conversationsByTab.map((conv) => (
                  <ConversationItem
                    key={conv.roomId || conv.id}
                    conversation={conv}
                    isActive={activeConversation?.roomId === conv.roomId}
                    currentUserId={currentUserId || 0}
                    onClick={() => selectConversation(conv)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${activeConversation ? 'flex' : 'hidden lg:flex'} col-span-12 lg:col-span-8 flex-col bg-white h-full`}>
            {activeConversation ? (
              <>
                {/* Chat Header - Fixed */}
                <div className="flex-shrink-0 p-3 border-b flex items-center justify-between bg-white">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActiveConversation(null)}
                      className="lg:hidden p-2 hover:bg-gray-100 rounded-full"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                        {activeConversation.type === 'GROUP'
                          ? <Users className="w-5 h-5" />
                          : (activeConversation.displayName || '?')[0]}
                      </div>
                      {activeConversation.type !== 'GROUP' && onlineUsers[activeConversation.otherUserId!] && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{activeConversation.displayName}</p>
                      <p className="text-xs text-gray-500">
                        {getTypingText() || (activeConversation.type === 'GROUP'
                          ? `${activeConversation.memberCount || 0} members`
                          : onlineUsers[activeConversation.otherUserId!] ? 'Online' : 'Offline')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600">
                      <Phone className="w-5 h-5" />
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600">
                      <Video className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Messages - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" style={{ maxHeight: 'calc(100vh - 350px)' }}>
                  {loadingMessages ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
                      <p className="text-sm">Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <MessageCircle className="w-16 h-16 mx-auto mb-3 opacity-20" />
                      <p className="font-medium">No messages yet</p>
                      <p className="text-sm">Start the conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {messages.map((msg, index) => (
                        <MessageBubble
                          key={`${msg.id}-${index}`}
                          message={msg}
                          isOwn={msg.senderId === currentUserId}
                          currentUserId={currentUserId || 0}
                          onReply={() => setReplyTo(msg)}
                          onEdit={() => { setEditingMessage(msg); setEditText(msg.content || '') }}
                          onDelete={(forEveryone) => deleteMessage(msg.id as number, forEveryone)}
                          showOptions={showMessageOptions}
                          setShowOptions={setShowMessageOptions}
                        />
                      ))}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply/Edit Header - Fixed */}
                {(replyTo || editingMessage) && (
                  <div className="flex-shrink-0 px-4 py-2 bg-gray-100 border-t flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <CornerUpLeft className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600">
                        {editingMessage ? 'Edit message' : `Reply to ${replyTo?.sender?.firstName || 'message'}`}
                      </span>
                      <span className="text-gray-400 truncate max-w-[200px]">
                        {editingMessage?.content || replyTo?.content}
                      </span>
                    </div>
                    <button onClick={() => { setReplyTo(null); setEditingMessage(null); setEditText('') }} className="p-1 hover:bg-gray-200 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Message Input - Fixed at bottom */}
                <div className="flex-shrink-0 p-3 border-t bg-white relative">
                  {showEmojiPicker && (
                    <div className="absolute bottom-16 left-2 z-10">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          setNewMessage(prev => prev + emojiData.emoji)
                          setShowEmojiPicker(false)
                        }}
                        width={300}
                        height={400}
                      />
                    </div>
                  )}

                  {/* File inputs */}
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'IMAGE')} />
                  <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleFileUpload(e, 'VIDEO')} />
                  <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => handleFileUpload(e, 'FILE')} />

                  {/* Edit mode */}
                  {editingMessage && (
                    <div className="mb-2 p-2 bg-blue-50 rounded flex items-center gap-2">
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') saveEditMessage() }}
                        className="flex-1 px-3 py-1 bg-white rounded border focus:ring-2 focus:ring-blue-500 outline-none"
                        autoFocus
                      />
                      <button onClick={saveEditMessage} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                        Save
                      </button>
                      <button onClick={() => { setEditingMessage(null); setEditText('') }} className="px-3 py-1 bg-gray-200 rounded text-sm">
                        Cancel
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 hover:bg-gray-100 rounded-full text-gray-600">
                      <Smile className="w-5 h-5" />
                    </button>
                    <button onClick={() => fileInputRef.current?.click()} className="p-2 hover:bg-gray-100 rounded-full text-gray-600">
                      <ImageIcon className="w-5 h-5" />
                    </button>
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => { setNewMessage(e.target.value); handleTyping() }}
                      onKeyPress={(e) => { if (e.key === 'Enter') sendMessage() }}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 bg-gray-100 rounded-full focus:ring-2 focus:ring-blue-500 outline-none"
                      disabled={uploadingFile}
                    />
                    {uploadingFile ? (
                      <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                    ) : (
                      <GovButton onClick={sendMessage} disabled={!newMessage.trim()}>
                        <Send className="w-4 h-4" />
                      </GovButton>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm">Choose from your existing chats or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </GovCard>

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        onStartChat={startConversation}
        currentUserId={currentUserId || 0}
      />

      {/* New Group Modal */}
      <NewGroupModal
        isOpen={showNewGroupModal}
        onClose={() => setShowNewGroupModal(false)}
        onCreateGroup={createGroup}
        currentUserId={currentUserId || 0}
      />
    </GovLayout>
  )
}

export default React.memo(MessagesPage)